import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { JsonForm, initializeFormValues, } from 'src/components/flows/RegistrationFlow/shared/JsonForm';
import styles from 'src/components/flows/styles/RegistrationForm.module.scss';
import { getSchoolRegistrationFormSchemaUrl } from 'src/config';
import ErrorPage from 'src/components/ErrorPage';
// CSS class names mapping for JsonForm
const formClassNames = {
    form: styles.form,
    field: styles.field,
    fieldLabel: styles.fieldLabel,
    inputWrapper: styles.inputWrapper,
    input: styles.input,
    inputDisabled: styles.inputDisabled,
    helperText: styles.helperText,
    dropdownWrapper: styles.dropdownWrapper,
    dropdown: styles.dropdown,
    dropdownIcon: styles.dropdownIcon,
    dropdownMulti: styles.dropdownMulti,
    radioGroup: styles.radioGroup,
    radioGroupHeader: styles.radioGroupHeader,
    radioOptions: styles.radioOptions,
    radioOption: styles.radioOption,
    radioOptionChecked: styles.checked,
    radioIcon: styles.radioIcon,
    radioLabel: styles.radioLabel,
    divider: styles.divider,
    termsWrapper: styles.termsWrapper,
    checkbox: styles.checkbox,
    termsText: styles.termsText,
    termsLink: styles.termsLink,
};
/**
 * SchoolRegistration step component
 * Collects registration information from school users
 * Uses JsonForm for rendering - all field knowledge is in the schema
 * Schema is loaded dynamically from remote URL at runtime
 */
const SchoolRegistration = ({ initialData, onDataChange, setTitle, onPageValid, }) => {
    const [formSchema, setFormSchema] = useState(null);
    const [isLoadingSchema, setIsLoadingSchema] = useState(true);
    const [schemaError, setSchemaError] = useState(null);
    const [formData, setFormData] = useState({});
    // Fetch the form schema on mount
    useEffect(() => {
        const fetchSchema = async () => {
            try {
                const schemaUrl = getSchoolRegistrationFormSchemaUrl();
                console.log('Fetching form schema from:', schemaUrl);
                const response = await fetch(schemaUrl);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                const schema = await response.json();
                setFormSchema(schema);
                setIsLoadingSchema(false);
                // Initialize form data with the fetched schema
                const initializedData = initializeFormValues(schema, initialData);
                setFormData(initializedData);
                console.log('Form schema loaded successfully');
            }
            catch (error) {
                const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                console.error('CRITICAL: Failed to load form schema', {
                    error: errorMessage,
                    schemaUrl: getSchoolRegistrationFormSchemaUrl(),
                    timestamp: new Date().toISOString(),
                });
                setSchemaError(errorMessage);
                setIsLoadingSchema(false);
            }
        };
        fetchSchema();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);
    useEffect(() => {
        setTitle?.('Register');
        onPageValid?.(false);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);
    // Sync form data to parent whenever it changes
    useEffect(() => {
        onDataChange?.(formData);
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [formData]);
    /**
     * Handles field value changes from JsonForm
     */
    const handleFieldChange = (fieldName, value) => {
        setFormData((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    };
    /**
     * Handles form validity changes from JsonForm
     * JsonForm validates both fields and terms acceptance
     */
    const handleValidityChange = (isValid) => {
        onPageValid?.(isValid);
    };
    // Determine which fields should be read-only
    // schoolName is read-only when a school has been selected (has schoolId)
    const readOnlyFields = formData.schoolId ? ['schoolName'] : [];
    // Show loading state while schema is being fetched
    if (isLoadingSchema) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.formWrapper, children: _jsx("p", { children: "Loading registration form..." }) }) }));
    }
    // Show error page if schema failed to load
    if (schemaError || !formSchema) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.formWrapper, children: _jsx(ErrorPage, { title: "Unable to Load Registration Form", message: "We're having trouble loading the registration form. This could be a temporary network issue or a problem with our servers.", technicalDetails: schemaError || 'Form schema not available', showRetry: true }) }) }));
    }
    return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.formWrapper, children: _jsx(JsonForm, { schema: formSchema, values: formData, onChange: handleFieldChange, onValidityChange: handleValidityChange, classNames: formClassNames, readOnlyFields: readOnlyFields }) }) }));
};
export default SchoolRegistration;
